/**
 * Which state a person's identity panel is in (USR-F1-T1) — pure, so it is
 * testable without a DOM.
 *
 * The server sends `identity` from its one reader (api policy/user-identity.ts),
 * already gated by the tenant's compliance frameworks:
 *
 *   null → 'unavailable' — the framework state could not be read. Show no
 *          value, no badge, no action; say so. Never "no framework".
 *   {}   → 'none'        — no active framework surfaces an identity field
 *          (decisions.md Users rail #1: hidden means absent).
 *   else → 'fields'      — render each PRESENT key and nothing else.
 */
import type { UserIdentity } from '@argusai/shared/schemas/user-identity';

export type IdentityPanelState = 'unavailable' | 'none' | 'fields';

export function identityPanelState(identity: UserIdentity | null | undefined): IdentityPanelState {
  if (identity === null || identity === undefined) return 'unavailable';
  return Object.keys(identity).length === 0 ? 'none' : 'fields';
}

/**
 * A person's US-person standing (USR-F1-T1-R1) — THE one place the web reads
 * it, so the roster tick, the roster filter, the roster count card and the
 * detail tab cannot disagree.
 *
 *   undefined     — the server sent no US-person answer (key absent, or the
 *                   identity could not be read). Render nothing; match no filter.
 *   'unverified'  — the stored fact is "not verified".
 *   'in_standing' — verified AND the server says in standing.
 *   'lapsed'      — verified, but the server does not say in standing (its
 *                   re-verification date passed). Fails closed: a verified
 *                   person whose standing is missing is NOT in standing.
 *
 * Standing is the server's `usPersonInStanding` (the clearance rule's
 * `usPersonStanding`); no date is compared here.
 */
export type UsPersonStanding = 'unverified' | 'in_standing' | 'lapsed';

export function usPersonStandingOf(identity: UserIdentity | null | undefined): UsPersonStanding | undefined {
  if (!identity || identity.usPersonVerified === undefined) return undefined;
  if (identity.usPersonVerified !== true) return 'unverified';
  return identity.usPersonInStanding === true ? 'in_standing' : 'lapsed';
}
